import React, { useEffect } from 'react';
import ChipItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/ChipItem';
import GenericSidebarFilter, {
  GenericSidebarFilterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';
import {
  FILTER_OPERATION,
  useSavedSearchBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useSavedSearchBuilder';
import { SlSidebarScrollableContainer } from 'src/components/BeaconV2Components/SlSidebar';

interface LegacySidebarFilterProps extends GenericSidebarFilterProps {
  filterParameter: string;
  transferableParameter?: string;
}

/**
 * Legacy sidebar filter for the segment builder V2. This component is used to filter the segments by a specific parameter such as keyword.
 */
const LegacySidebarFilter = (props: LegacySidebarFilterProps) => {
  const { formData, updateSavedSearch } = useSavedSearchBuilder();
  const currentInputs = formData.termFilters[props.filterParameter].values.map((value) => value.i) ?? [];
  const transferrableInputs = formData.termFilters[props.transferableParameter]?.values.map((value) => value.i) ?? [];

  useEffect(() => {
    if (transferrableInputs.length > 0) {
      new Set(transferrableInputs).forEach((input) => {
        if (!currentInputs.includes(input)) {
          updateSavedSearch({
            key: props.filterParameter,
            transferKey: props.transferableParameter,
            newValue: input,
            operation: FILTER_OPERATION.Transfer
          });
        }
      });
    }
  }, [props.transferableParameter]);

  /**
   * Placeholder state for the current input. Tracks the user input before the user presses enter to add a term to the list of currentInputs.
   */
  const [currentInput, setCurrentInput] = React.useState<string>('');

  /**
   * Renders the list of items as chips
   */
  const renderItems = React.useCallback(() => {
    return (
      <SlSidebarScrollableContainer marginTop="12px" marginBottom={currentInputs?.length ? '32px' : '16px'}>
        {currentInputs.map((input, index) => (
          <ChipItem
            key={index}
            label={input}
            handleDelete={() =>
              updateSavedSearch({ key: props.filterParameter, newValue: input, operation: FILTER_OPERATION.Delete })
            }
          />
        ))}
      </SlSidebarScrollableContainer>
    );
  }, [currentInputs, updateSavedSearch, props.filterParameter]);

  /**
   * Handles the change of the input
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  /**
   * Handles the enter key press
   */
  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      updateSavedSearch({ key: props.filterParameter, newValue: currentInput, operation: FILTER_OPERATION.Add });
      setCurrentInput('');
    }
  };

  const handleClear = () => {
    updateSavedSearch({ key: props.filterParameter, operation: FILTER_OPERATION.Clear });
  };

  return (
    <GenericSidebarFilter
      {...props}
      renderItems={renderItems}
      handleClear={handleClear}
      includeClear={currentInputs?.length > 0}
      sidebarInputProps={{
        ...props.sidebarInputProps,
        value: currentInput,
        onChange: handleChange,
        onKeyPress: handleEnter
      }}
    />
  );
};

export default React.memo(LegacySidebarFilter);
